/**
 * Android Kiosk Mode Services
 * Handles auto-start, watchdog, heartbeat, and device management for Android displays
 */

import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { CapacitorHttp, Capacitor } from '@capacitor/core';
import { getApiBaseUrl_dynamic, getIsAndroid } from '../config/config';
import ApkInstaller from '../plugins/apk-installer';
import AppRestarter from '../plugins/app-restarter';
import DeviceId from '../plugins/device-id';
import { getFlightDataStatus, getComprehensiveStatus, BlankReason } from './flightDataMonitor.js';
import {
  initializeErrorHandlers,
  startPeriodicHealthChecks,
  checkUIHealth,
  getUIHealthStatus,
  onHealthChange
} from './uiHealthMonitor.js';
import { getBoardHealthForHeartbeat } from './boardHealthStore.js';

// Heartbeat interval (1 minute)
const HEARTBEAT_INTERVAL = 60000;

// Health check interval (5 seconds)
const HEALTH_CHECK_INTERVAL = 5000;

// Auto-reload interval (1 hour) - safety net for stuck app state
const AUTO_RELOAD_INTERVAL = 1 * 60 * 60 * 1000; // 1 hour in milliseconds

// Stale data auto-reload threshold (10 minutes with active socket)
const STALE_DATA_RELOAD_THRESHOLD = 10 * 60 * 1000; // 10 minutes in milliseconds

// Daily quiet-time restart hour (4 AM local time)
const QUIET_TIME_RESTART_HOUR = 4;

// UI health reload threshold (2 minutes of unhealthy UI = reload)
const UI_UNHEALTHY_RELOAD_THRESHOLD_MS = 2 * 60 * 1000;

// Reload storm prevention - exponential backoff
const MIN_RELOAD_INTERVAL_MS = 60 * 1000; // Minimum 60 seconds between reloads
const MAX_RELOAD_BACKOFF_MS = 10 * 60 * 1000; // Max 10 minutes backoff
const RELOAD_BACKOFF_MULTIPLIER = 2;

// Store device info globally
let deviceInfo = null;
let heartbeatTimer = null;
let healthCheckTimer = null;
let autoReloadTimer = null;
let quietTimeRestartTimer = null;
let uiHealthCheckCleanup = null;
let lastHeartbeatSuccess = Date.now();
let appStartTime = Date.now();
let uiUnhealthySince = null; // Track when UI became unhealthy

// Connectivity state machine (prevents reload storms)
let _isNetworkAvailable = true;
let _lastReloadTimestamp = 0;
let _reloadBackoffMs = MIN_RELOAD_INTERVAL_MS;
let _consecutiveReloadAttempts = 0;

// Reload tracking (stored in localStorage to persist across reloads)
const RELOAD_HISTORY_KEY = 'fids_reload_history';
const LAST_RELOAD_TIME_KEY = 'fids_last_reload_time';
const RELOAD_BACKOFF_KEY = 'fids_reload_backoff';

/**
 * Record a reload event (call before triggering reload)
 */
function recordReload() {
  try {
    const history = JSON.parse(localStorage.getItem(RELOAD_HISTORY_KEY) || '[]');
    history.push(Date.now());
    // Keep only last 2 hours of history
    const twoHoursAgo = Date.now() - 2 * 60 * 60 * 1000;
    const filtered = history.filter(ts => ts > twoHoursAgo);
    localStorage.setItem(RELOAD_HISTORY_KEY, JSON.stringify(filtered));
    localStorage.setItem(LAST_RELOAD_TIME_KEY, String(Date.now()));
  } catch (e) {
    console.error('Failed to record reload:', e);
  }
}

/**
 * Check if we're in a reload storm (too many reloads recently)
 */
function isReloadStorm() {
  const reloadsInLastHour = getReloadCount1h();
  // If more than 5 reloads in the last hour, we're in a storm
  return reloadsInLastHour > 5;
}

/**
 * Get time since last reload in milliseconds
 */
function getTimeSinceLastReload() {
  try {
    const lastReload = parseInt(localStorage.getItem(LAST_RELOAD_TIME_KEY) || '0', 10);
    return Date.now() - lastReload;
  } catch {
    return Infinity;
  }
}

/**
 * Get current reload backoff (exponential)
 */
function getReloadBackoff() {
  try {
    const backoff = parseInt(localStorage.getItem(RELOAD_BACKOFF_KEY) || String(MIN_RELOAD_INTERVAL_MS), 10);
    return Math.min(backoff, MAX_RELOAD_BACKOFF_MS);
  } catch {
    return MIN_RELOAD_INTERVAL_MS;
  }
}

/**
 * Increase reload backoff (exponential)
 */
function increaseReloadBackoff() {
  try {
    const current = getReloadBackoff();
    const next = Math.min(current * RELOAD_BACKOFF_MULTIPLIER, MAX_RELOAD_BACKOFF_MS);
    localStorage.setItem(RELOAD_BACKOFF_KEY, String(next));
    _reloadBackoffMs = next;
    _consecutiveReloadAttempts++;
    return next;
  } catch {
    return MIN_RELOAD_INTERVAL_MS;
  }
}

/**
 * Reset reload backoff (call when app is stable)
 */
function resetReloadBackoff() {
  try {
    localStorage.setItem(RELOAD_BACKOFF_KEY, String(MIN_RELOAD_INTERVAL_MS));
    _reloadBackoffMs = MIN_RELOAD_INTERVAL_MS;
    _consecutiveReloadAttempts = 0;
  } catch {
    // Ignore errors
  }
}

/**
 * Safe reload with backoff - prevents reload storms
 * Returns true if reload was triggered, false if blocked by backoff
 */
function safeReload(reason = 'unknown') {
  const now = Date.now();
  const timeSinceLastReload = getTimeSinceLastReload();
  const currentBackoff = getReloadBackoff();

  // Check if we're in a reload storm
  if (isReloadStorm()) {
    console.warn(`🛑 Reload storm detected! Blocking reload (reason: ${reason})`);
    console.warn(`   ${getReloadCount1h()} reloads in the last hour`);
    console.warn(`   Will retry after backoff expires`);
    increaseReloadBackoff();
    return false;
  }

  // Check backoff period
  if (timeSinceLastReload < currentBackoff) {
    const waitTime = Math.ceil((currentBackoff - timeSinceLastReload) / 1000);
    console.warn(`⏳ Reload blocked by backoff (reason: ${reason})`);
    console.warn(`   Time since last reload: ${Math.floor(timeSinceLastReload / 1000)}s`);
    console.warn(`   Current backoff: ${Math.floor(currentBackoff / 1000)}s`);
    console.warn(`   Wait ${waitTime}s more before next reload`);
    return false;
  }

  // Safe to reload
  console.log(`🔄 Safe reload triggered (reason: ${reason})`);
  console.log(`   Time since last reload: ${Math.floor(timeSinceLastReload / 1000)}s`);

  // Increase backoff for next potential reload
  increaseReloadBackoff();

  // Record and reload
  recordReload();
  _lastReloadTimestamp = now;
  window.location.reload();
  return true;
}

/**
 * Update network availability state
 */
function setNetworkAvailable(available) {
  const wasAvailable = _isNetworkAvailable;
  _isNetworkAvailable = available;

  if (!wasAvailable && available) {
    console.log('🟢 Network connectivity restored');
    // Reset backoff when network recovers and app has been stable
    if (getTimeSinceLastReload() > 5 * 60 * 1000) {
      resetReloadBackoff();
    }
  } else if (wasAvailable && !available) {
    console.log('🔴 Network connectivity lost');
  }
}

/**
 * Get reload count in last hour
 */
function getReloadCount1h() {
  try {
    const history = JSON.parse(localStorage.getItem(RELOAD_HISTORY_KEY) || '[]');
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return history.filter(ts => ts > oneHourAgo).length;
  } catch (e) {
    return 0;
  }
}

/**
 * Submit device registration request to backend
 */
async function submitRegistrationRequest(deviceInfo) {
  try {
    // Determine display mode from MainActivity configuration or window
    const displayMode = window.ANDROID_DISPLAY_MODE ||
      (window.ANDROID_START_ROUTE?.includes('departure') ? 'departures' : 'arrivals');

    const apiUrl = getApiBaseUrl_dynamic();
    const registrationUrl = `${apiUrl}/api/device/register`;

    console.log('🔗 Registration API URL:', registrationUrl);
    console.log('📱 Device ID:', deviceInfo.deviceId);
    console.log('🏷️ Display mode:', displayMode);

    const response = await CapacitorHttp.post({
      url: registrationUrl,
      headers: {
        'Content-Type': 'application/json'
      },
      data: {
        deviceId: deviceInfo.deviceId,
        deviceInfo: {
          model: deviceInfo.model,
          manufacturer: deviceInfo.manufacturer,
          osVersion: deviceInfo.osVersion,
          platform: deviceInfo.platform,
          isVirtual: deviceInfo.isVirtual,
          displayMode: displayMode
        },
        requestedDescription: `${deviceInfo.manufacturer} ${deviceInfo.model} (${displayMode}) - Auto-registered`
      }
    });

    console.log('📝 Registration response status:', response.status);
    console.log('📝 Registration response data:', JSON.stringify(response.data, null, 2));
    return response.data;
  } catch (error) {
    console.error('❌ Failed to submit registration request:', error);
    console.error('❌ Error details:', error.message || error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    return null;
  }
}

/**
 * Poll for registration approval status
 */
async function checkRegistrationStatus(deviceId) {
  try {
    const response = await CapacitorHttp.get({
      url: `${getApiBaseUrl_dynamic()}/api/device/register/status/${deviceId}`,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('❌ Failed to check registration status:', error);
    return null;
  }
}

/**
 * Validate existing device token with backend
 */
async function validateDeviceToken(token) {
  try {
    const response = await CapacitorHttp.post({
      url: `${getApiBaseUrl_dynamic()}/api/heartbeat`,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      data: {
        deviceId: deviceInfo.deviceId,
        version: await getAppVersion(),
        status: 'online',
        timestamp: new Date().toISOString(),
        metadata: {
          displayMode: window.ANDROID_DISPLAY_MODE || 'unknown'
        }
      }
    });

    return response.status === 200;
  } catch (error) {
    console.warn('⚠️ Token validation failed:', error);
    return false;
  }
}

/**
 * Initialize device token from environment variable or auto-registration
 * Priority:
 * 1. Build-time token (process.env.REACT_APP_DEVICE_TOKEN)
 * 2. Existing token in localStorage (validated with backend)
 * 3. Auto-registration (submit request and poll for approval)
 */
export async function initializeDeviceToken() {
  const isAndroid = getIsAndroid();
  console.log('🔐 initializeDeviceToken() called');
  console.log('📱 isAndroid:', isAndroid);
  console.log('📱 deviceInfo:', deviceInfo ? `${deviceInfo.manufacturer} ${deviceInfo.model}` : 'NOT SET');

  // Check if device token is already in localStorage
  const existingToken = localStorage.getItem('fids_device_token');
  console.log('🔑 Existing token in localStorage:', existingToken ? `${existingToken.substring(0, 12)}...` : 'NONE');

  // Check if a token was injected during build via environment variable
  const buildTimeToken = process.env.REACT_APP_DEVICE_TOKEN;

  // Priority 1: Build-time token
  if (buildTimeToken && buildTimeToken !== 'PLACEHOLDER') {
    if (existingToken !== buildTimeToken) {
      console.log('🔑 Setting device token from build configuration');
      localStorage.setItem('fids_device_token', buildTimeToken);
      console.log('✅ Device token configured successfully');
    } else {
      console.log('✅ Device token already configured from build');
    }
    return;
  }

  // Priority 2: Existing token - VALIDATE IT FIRST
  if (existingToken) {
    console.log('🔍 Found existing token, validating with backend...');

    // Validate the token by attempting a heartbeat
    const isValid = await validateDeviceToken(existingToken);

    if (isValid) {
      console.log('✅ Existing device token is valid');
      return;
    } else {
      console.warn('❌ Existing token is invalid (device may have been deleted from admin panel)');
      console.log('🗑️ Clearing invalid token and re-registering...');
      localStorage.removeItem('fids_device_token');
      // Continue to auto-registration below
    }
  }

  // Priority 3: Auto-registration
  if (!getIsAndroid() || !deviceInfo) {
    console.warn('⚠️ No device token configured. Device info not yet available for auto-registration.');
    return;
  }

  console.log('🔄 No device token found. Starting auto-registration...');

  // Submit registration request
  const registrationResult = await submitRegistrationRequest(deviceInfo);

  if (!registrationResult) {
    console.error('❌ Failed to submit registration request. Device will retry on next startup.');
    return;
  }

  if (registrationResult.status === 'approved' && registrationResult.token) {
    // Already approved (re-registration case)
    console.log('✅ Device already approved! Setting token...');
    localStorage.setItem('fids_device_token', registrationResult.token);
    console.log('✅ Device token configured successfully');
    return;
  }

  if (registrationResult.status === 'pending') {
    console.log('⏳ Registration request pending approval...');
    console.log('📋 Device ID:', deviceInfo.deviceId);
    console.log('📱 Model:', deviceInfo.model);
    console.log('🏢 Manufacturer:', deviceInfo.manufacturer);
    console.log('');
    console.log('⚠️ Admin must approve this device in the Fleet Management dashboard');
    console.log('🔗 Dashboard: ' + getApiBaseUrl_dynamic() + '/admin/fleet/device-registrations');
    console.log('');
    console.log('⏱️ Checking for approval every 30 seconds...');

    // Start polling for approval (every 30 seconds)
    const pollInterval = setInterval(async () => {
      const status = await checkRegistrationStatus(deviceInfo.deviceId);

      if (status && status.status === 'approved' && status.token) {
        clearInterval(pollInterval);
        console.log('🎉 Device registration approved!');
        console.log('✅ Received token, configuring device...');
        localStorage.setItem('fids_device_token', status.token);
        console.log('✅ Device token configured successfully');
        console.log('🔄 Reloading app to activate fleet management features...');

        // Reload the app to start heartbeat with new token
        // Ensure we preserve the correct route for the flavor
        setTimeout(() => {
          // Get the target route from localStorage (most reliable) or window variable
          const storedRoute = localStorage.getItem('fids_start_route');
          const targetRoute = storedRoute || window.ANDROID_START_ROUTE || window.location.hash.replace('#', '') || '/';

          console.log(`📍 Current route before reload: ${window.location.hash}`);
          console.log(`📍 Stored route: ${storedRoute}`);
          console.log(`📍 Target route: ${targetRoute}`);
          console.log(`📍 ANDROID_START_ROUTE: ${window.ANDROID_START_ROUTE}`);

          // Navigate to the correct route based on the flavor, then reload
          if (!window.location.hash.includes(targetRoute.replace('/', ''))) {
            console.log(`🔀 Navigating to ${targetRoute} before reload`);
            window.location.hash = targetRoute;
            setTimeout(() => {
              console.log(`🔄 Reloading at ${window.location.hash}`);
              recordReload();
              window.location.reload();
            }, 200);
          } else {
            console.log(`✅ Already on correct route (${window.location.hash}), reloading`);
            recordReload();
            window.location.reload();
          }
        }, 2000); // Wait 2 seconds to let user see the success message
      } else if (status && status.status === 'rejected') {
        clearInterval(pollInterval);
        console.error('❌ Device registration rejected!');
        console.error('Reason:', status.rejectionReason || 'No reason provided');
        console.log('💡 Contact your administrator to re-register this device');
      }
    }, 30000); // Poll every 30 seconds

    // Store the interval ID so we can clear it on shutdown
    window._registrationPollInterval = pollInterval;
  }
}

/**
 * Initialize device info
 */
export async function initializeDeviceInfo() {
  if (!getIsAndroid()) {
    console.log('⚠️ Not running on Android, skipping device initialization');
    return null;
  }

  try {
    const info = await Device.getInfo();

    // Use real Android Settings.Secure.ANDROID_ID (same as adb shell settings get secure android_id)
    let deviceId;
    try {
      const { androidId } = await DeviceId.getAndroidId();
      if (androidId) {
        deviceId = androidId;
        console.log('📱 Got real ANDROID_ID:', deviceId);
      }
    } catch (e) {
      console.warn('⚠️ DeviceId plugin failed, falling back to Capacitor ID:', e);
    }

    // Fallback to Capacitor's ID if native plugin fails
    if (!deviceId) {
      const id = await Device.getId();
      deviceId = id.identifier;
      console.log('📱 Using Capacitor fallback ID:', deviceId);
    }

    deviceInfo = {
      deviceId: deviceId,
      model: info.model,
      manufacturer: info.manufacturer,
      osVersion: info.osVersion,
      platform: info.platform,
      isVirtual: info.isVirtual
    };

    console.log('📱 Device initialized:', deviceInfo);
    return deviceInfo;
  } catch (error) {
    console.error('❌ Failed to initialize device info:', error);
    return null;
  }
}

/**
 * Get app version
 */
export async function getAppVersion() {
  if (!getIsAndroid()) return 'web';

  try {
    const info = await App.getInfo();
    return info.version;
  } catch (error) {
    console.error('❌ Failed to get app version:', error);
    return 'unknown';
  }
}

/**
 * Send heartbeat to fleet management server
 */
export async function sendHeartbeat() {
  if (!getIsAndroid() || !deviceInfo) {
    return { success: false, reason: 'not_android' };
  }

  try {
    const version = await getAppVersion();
    const network = await Network.getStatus();
    const battery = await Device.getBatteryInfo().catch(() => ({ batteryLevel: 1, isCharging: true }));

    // Determine display mode/flavor from MainActivity or localStorage
    const displayMode = window.ANDROID_DISPLAY_MODE || localStorage.getItem('fids_display_mode') || 'all';

    // Get comprehensive flight data status (includes blank reason classification)
    const flightDataStatus = getComprehensiveStatus();

    // Get UI health status
    const uiHealth = getUIHealthStatus();

    // Determine if board is in a blank/recovery state
    const isBlankBoard = flightDataStatus.isBlank || !uiHealth.healthy;

    // Board-specific health from boardHealthStore (written by probe + self-healing)
    const boardHealth = getBoardHealthForHeartbeat();

    const payload = {
      deviceId: deviceInfo.deviceId,
      version: version,
      status: 'online',
      timestamp: new Date().toISOString(),
      metadata: {
        displayMode: displayMode, // 'arrivals', 'departures', or 'all'
        model: deviceInfo.model,
        manufacturer: deviceInfo.manufacturer,
        osVersion: deviceInfo.osVersion,
        networkConnected: network.connected,
        connectionType: network.connectionType,
        batteryLevel: battery.batteryLevel,
        isCharging: battery.isCharging,
        uptime: getUptime(),
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        // Flight data metrics (from comprehensive status)
        lastFlightUpdate: flightDataStatus.lastFlightUpdate,
        flightDataStale: flightDataStatus.flightDataStale,
        socketConnected: flightDataStatus.socketConnected,
        flightsReceived: flightDataStatus.flightsReceived,
        flightsDisplayed: flightDataStatus.flightsDisplayed,
        domDataRowCount: flightDataStatus.domDataRowCount,
        timeSinceUpdateSeconds: flightDataStatus.timeSinceUpdateSeconds,
        // Blank state classification
        blankReason: flightDataStatus.blankReason,
        isBlankBoard: isBlankBoard,
        lastHealthyRenderAt: flightDataStatus.lastHealthyRenderAt,
        // UI health metrics
        renderOk: (uiHealth.healthy && !flightDataStatus.isBlank) || boardHealth.boardRenderOk,
        lastRenderAt: uiHealth.lastRenderTimestamp ? new Date(uiHealth.lastRenderTimestamp).toISOString() : null,
        jsErrorCount5m: uiHealth.recentJsErrors || 0,
        reloadCount1h: getReloadCount1h(),
        // Board self-healing telemetry (from boardHealthStore)
        boardRenderOk: boardHealth.boardRenderOk,
        boardRenderedAt: boardHealth.boardRenderedAt,
        boardVisibleRowCount: boardHealth.boardVisibleRowCount,
        boardContainerRect: boardHealth.boardContainerRect,
        boardBlankReason: boardHealth.boardBlankReason,
        boardLastRecoveryAction: boardHealth.boardLastRecoveryAction,
        boardRecoveryCount1h: boardHealth.boardRecoveryCount1h,
        boardRecoveryCount24h: boardHealth.boardRecoveryCount24h,
        boardCrashCount5m: boardHealth.boardCrashCount5m,
        boardCrashCount24h: boardHealth.boardCrashCount24h,
        stableFallbackActive: boardHealth.stableFallbackActive,
        boardLastErrorShort: boardHealth.boardLastErrorShort,
      }
    };

    // Get device token from storage (set during setup)
    const deviceToken = localStorage.getItem('fids_device_token');
    if (!deviceToken) {
      // Only log once every 5 minutes to avoid spam
      const timeSinceAppStart = Date.now() - appStartTime;
      if (timeSinceAppStart < 60000 || timeSinceAppStart % (5 * 60 * 1000) < 60000) {
        console.log('⏳ Waiting for device registration approval...');
      }
      return { success: false, reason: 'no_token' };
    }

    const response = await CapacitorHttp.post({
      url: `${getApiBaseUrl_dynamic()}/api/heartbeat`,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${deviceToken}`
      },
      data: payload
    });

    if (response.status === 200) {
      lastHeartbeatSuccess = Date.now();
      console.log('💓 Heartbeat sent successfully');

      // DEBUG: Log full response for troubleshooting
      console.log('📥 Heartbeat response:', JSON.stringify(response.data, null, 2));

      // Check for APK updates in response
      if (response.data?.data?.updateAvailable && response.data?.data?.update) {
        const updateInfo = response.data.data.update;
        console.log('🎯 UPDATE INSTRUCTION RECEIVED!');
        console.log(`   Version: ${updateInfo.version}`);
        console.log(`   APK URL: ${updateInfo.apkUrl || 'MISSING!'}`);
        console.log(`   DEB URL: ${updateInfo.debUrl || 'N/A'}`);
        console.log(`   Force: ${updateInfo.forceUpdateNow}`);
        console.log(`   Rollout ID: ${updateInfo.rolloutId}`);

        // Validate that we have a download URL
        if (!updateInfo.apkUrl && !updateInfo.debUrl) {
          console.error('❌ No download URL provided in update instruction!');
          console.error('   Update Info:', JSON.stringify(updateInfo, null, 2));
          return { success: true, data: response.data };
        }

        // Check if Device Owner for automatic installation
        let isDeviceOwner = false;
        try {
          const result = await ApkInstaller.isDeviceOwner();
          isDeviceOwner = result.isDeviceOwner;
        } catch (error) {
          // Plugin may not be available
        }

        // Install immediately if:
        // 1. Force update requested, OR
        // 2. Device Owner mode is enabled (silent installation capability)
        const shouldInstallNow = updateInfo.forceUpdateNow || isDeviceOwner;

        if (shouldInstallNow) {
          if (isDeviceOwner) {
            console.log('🔐 Device Owner mode enabled - installing update immediately (silent)');
          } else {
            console.log('⚡ Force update requested, downloading APK...');
          }
          await downloadAndInstallAPK(updateInfo);
        } else {
          console.log('📋 Update queued for next restart (manual installation required)');
          // Store update info for later installation
          localStorage.setItem('fids_pending_update', JSON.stringify(updateInfo));
        }
      } else {
        console.log('✅ No update available, device is up to date');
      }

      // Check for pending device commands (restart, reload)
      if (response.data?.data?.command) {
        const command = response.data.data.command;
        console.log(`📥 Received command from server: ${command.type}`);
        console.log(`   Command ID: ${command.id}`);
        console.log(`   Reason: ${command.reason}`);

        // Execute the command
        await executeDeviceCommand(command);
      }

      return { success: true, data: response.data };
    } else {
      console.error('❌ Heartbeat failed:', response.status);
      return { success: false, reason: 'server_error', status: response.status };
    }
  } catch (error) {
    console.error('❌ Heartbeat error:', error);
    return { success: false, reason: 'network_error', error };
  }
}

/**
 * Execute a device command received from the server
 */
async function executeDeviceCommand(command) {
  const { id, type, reason } = command;

  console.log(`🔧 Executing command: ${type} (ID: ${id})`);
  console.log(`   Reason: ${reason}`);

  try {
    if (type === 'restart') {
      console.log('🔄 Executing RESTART command...');
      console.log('   This will kill the app process and relaunch.');

      // Store restart info for tracking
      localStorage.setItem('fids_last_restart', JSON.stringify({
        commandId: id,
        reason: reason,
        timestamp: new Date().toISOString(),
        type: 'remote_command'
      }));

      // Acknowledge before restarting (best effort)
      await acknowledgeCommand(id, true);

      // Give time for acknowledgment to be sent
      await new Promise(resolve => setTimeout(resolve, 500));

      // Execute the restart via native plugin
      try {
        await AppRestarter.restartApp({
          reason: reason || 'admin_request',
          delayMs: 1000 // 1 second delay for clean shutdown
        });
      } catch (pluginError) {
        console.error('❌ Native restart failed, falling back to page reload:', pluginError);
        // Fallback: if native restart fails, at least reload the page
        // Use safeReload but allow it since this is an explicit admin command
        resetReloadBackoff();
        safeReload('remote_command_restart_fallback');
      }

    } else if (type === 'reload') {
      console.log('🔃 Executing RELOAD command...');
      console.log('   This will refresh the WebView content.');

      // Store reload info for tracking
      localStorage.setItem('fids_last_reload', JSON.stringify({
        commandId: id,
        reason: reason,
        timestamp: new Date().toISOString(),
        type: 'remote_command'
      }));

      // Acknowledge before reloading
      await acknowledgeCommand(id, true);

      // Give time for acknowledgment to be sent
      await new Promise(resolve => setTimeout(resolve, 500));

      // Execute the reload
      try {
        await AppRestarter.reloadWebView({
          reason: reason || 'admin_request'
        });
      } catch (pluginError) {
        console.error('❌ Native reload failed, using standard reload:', pluginError);
        // Fallback: use safeReload but allow it since this is an explicit admin command
        resetReloadBackoff();
        safeReload('remote_command_reload_fallback');
      }

    } else {
      console.warn(`⚠️ Unknown command type: ${type}`);
      await acknowledgeCommand(id, false, `Unknown command type: ${type}`);
    }

  } catch (error) {
    console.error(`❌ Failed to execute command ${type}:`, error);
    // Try to acknowledge the failure
    try {
      await acknowledgeCommand(id, false, error.message);
    } catch (ackError) {
      console.error('❌ Also failed to acknowledge error:', ackError);
    }
  }
}

/**
 * Acknowledge a command execution to the server
 */
async function acknowledgeCommand(commandId, success, error = null) {
  try {
    const deviceToken = localStorage.getItem('fids_device_token');
    if (!deviceToken) {
      console.warn('⚠️ No device token, cannot acknowledge command');
      return;
    }

    const response = await CapacitorHttp.post({
      url: `${getApiBaseUrl_dynamic()}/api/heartbeat/command/${commandId}/acknowledge`,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${deviceToken}`
      },
      data: {
        success: success,
        error: error
      }
    });

    if (response.status === 200) {
      console.log(`✅ Command ${commandId} acknowledged`);
    } else {
      console.warn(`⚠️ Command acknowledgment returned status ${response.status}`);
    }
  } catch (err) {
    console.error('❌ Failed to acknowledge command:', err);
  }
}

/**
 * Manually trigger a restart (for hidden corner trigger)
 * This is exported for use by UI components
 */
export async function triggerManualRestart(reason = 'manual_trigger') {
  console.log('🔄 Manual restart triggered');
  console.log(`   Reason: ${reason}`);

  // Store restart info
  localStorage.setItem('fids_last_restart', JSON.stringify({
    reason: reason,
    timestamp: new Date().toISOString(),
    type: 'manual_trigger'
  }));

  try {
    await AppRestarter.restartApp({
      reason: reason,
      delayMs: 1000
    });
  } catch (error) {
    console.error('❌ Manual restart failed:', error);
    // Fallback: manual restarts bypass backoff since user explicitly requested
    resetReloadBackoff();
    safeReload('manual_restart_fallback');
  }
}

/**
 * Start heartbeat service
 */
export function startHeartbeat() {
  if (!getIsAndroid()) {
    console.log('⚠️ Not on Android, heartbeat disabled');
    return;
  }

  // Stop existing timer if any
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
  }

  // Send initial heartbeat
  sendHeartbeat();

  // Set up recurring heartbeat
  heartbeatTimer = setInterval(() => {
    sendHeartbeat();
  }, HEARTBEAT_INTERVAL);

  console.log('💓 Heartbeat service started (every 60s)');
}

/**
 * Stop heartbeat service
 */
export function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
    console.log('💓 Heartbeat service stopped');
  }
}

/**
 * Get app uptime in seconds
 */
function getUptime() {
  return Math.floor((Date.now() - appStartTime) / 1000);
}

/**
 * Check for app updates
 */
export async function checkForUpdates() {
  if (!getIsAndroid() || !deviceInfo) {
    return { updateAvailable: false };
  }

  try {
    const currentVersion = await getAppVersion();
    const deviceToken = localStorage.getItem('fids_device_token');

    if (!deviceToken) {
      console.warn('⚠️ No device token, cannot check for updates');
      return { updateAvailable: false };
    }

    const response = await CapacitorHttp.get({
      url: `${getApiBaseUrl_dynamic()}/admin/artifacts/latest`,
      headers: {
        'Authorization': `Bearer ${deviceToken}`
      }
    });

    if (response.status === 200 && response.data) {
      const latestVersion = response.data.version;

      if (compareVersions(latestVersion, currentVersion) > 0) {
        console.log('🔄 Update available:', latestVersion, '(current:', currentVersion + ')');
        return {
          updateAvailable: true,
          currentVersion,
          latestVersion,
          downloadUrl: response.data.downloadUrl,
          changelog: response.data.changelog
        };
      }
    }

    return { updateAvailable: false, currentVersion };
  } catch (error) {
    console.error('❌ Failed to check for updates:', error);
    return { updateAvailable: false, error };
  }
}

/**
 * Compare version strings (semver-like)
 * @returns {number} 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 */
function compareVersions(v1, v2) {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;

    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }

  return 0;
}

/**
 * Health check - monitors app health and triggers self-healing
 * Uses safeReload to prevent reload storms
 */
function healthCheck() {
  const now = Date.now();
  const timeSinceLastHeartbeat = now - lastHeartbeatSuccess;
  const timeSinceAppStart = now - appStartTime;

  // Give 10 minutes grace period after app starts for device registration
  const gracePeriod = 10 * 60 * 1000; // 10 minutes
  if (timeSinceAppStart < gracePeriod) {
    // During grace period, only log warnings, don't trigger self-healing
    if (timeSinceLastHeartbeat > 5 * 60 * 1000) {
      console.warn('⚠️ Heartbeat not yet successful (device may be pending registration)');
    }
    return;
  }

  // === UI HEALTH CHECK (fast recovery for blank screens) ===
  const uiHealth = checkUIHealth();

  // The board-specific probe (MutationObserver) is more reliable than the
  // generic uiHealthMonitor DOM query, which can miss rows during page
  // transitions.  If the board probe confirms rendering is healthy, trust it.
  const boardHealth = getBoardHealthForHeartbeat();
  const boardOverridesUI = boardHealth.boardRenderOk
    && boardHealth.boardBlankReason === 'HEALTHY'
    && boardHealth.boardVisibleRowCount > 0;

  if (!uiHealth.healthy && boardOverridesUI) {
    // Board probe says healthy — suppress generic UI monitor false positive
    if (uiUnhealthySince) {
      console.log('✅ Board probe override: UI monitor unhealthy but board renders OK'
        + ` (rows=${boardHealth.boardVisibleRowCount}). Resetting UI unhealthy timer.`);
      uiUnhealthySince = null;
    }
  } else if (!uiHealth.healthy && !boardOverridesUI) {
    // Track when UI became unhealthy
    if (!uiUnhealthySince) {
      uiUnhealthySince = now;
      console.warn('🚨 UI health check: UNHEALTHY detected', {
        timeSinceRender: `${uiHealth.timeSinceRenderSeconds}s`,
        renderStale: uiHealth.renderStale,
        domHealthy: uiHealth.domHealthy,
        domReason: uiHealth.domReason,
        recentErrors: uiHealth.recentJsErrors
      });
    }

    const uiUnhealthyDuration = now - uiUnhealthySince;
    if (uiUnhealthyDuration >= UI_UNHEALTHY_RELOAD_THRESHOLD_MS) {
      console.error(`🚨 UI unhealthy for ${Math.floor(uiUnhealthyDuration / 1000)}s`);
      console.error('   Reason:', uiHealth.domReason || 'Render stale or JS errors');
      if (uiHealth.lastJsError) {
        console.error('   Last JS error:', uiHealth.lastJsError.message);
      }
      // Use safe reload with backoff
      safeReload('ui_unhealthy');
      return;
    }
  } else {
    // UI recovered - reset backoff if stable for a while
    if (uiUnhealthySince) {
      const recoveryTime = Math.floor((now - uiUnhealthySince) / 1000);
      console.log(`✅ UI health recovered after ${recoveryTime}s`);
      uiUnhealthySince = null;
      // Reset backoff after successful recovery
      if (getTimeSinceLastReload() > 5 * 60 * 1000) {
        resetReloadBackoff();
      }
    }
  }

  // === HEARTBEAT CHECK ===
  // After grace period, if no heartbeat success in 5 minutes, something is wrong
  if (timeSinceLastHeartbeat > 5 * 60 * 1000) {
    console.error('❌ Health check failed: No heartbeat success in 5 minutes');
    setNetworkAvailable(false);

    // Attempt self-healing (non-reload recovery first)
    attemptSelfHealing();
    return;
  } else {
    // Heartbeat is working, network is available
    setNetworkAvailable(true);
  }

  // === STALE FLIGHT DATA CHECK ===
  // Check for stale flight data - if socket is connected but no updates for 10+ minutes
  const flightStatus = getFlightDataStatus();
  const staleThresholdSeconds = STALE_DATA_RELOAD_THRESHOLD / 1000; // Convert ms to seconds
  if (flightStatus.socketConnected &&
      flightStatus.flightDataStale &&
      flightStatus.timeSinceUpdateSeconds !== null &&
      flightStatus.timeSinceUpdateSeconds > staleThresholdSeconds) {
    console.warn('⚠️ Flight data stale for 10+ minutes while socket connected');
    console.warn(`   Last update: ${flightStatus.lastFlightUpdate || 'never'}`);
    console.warn(`   Time since update: ${Math.floor(flightStatus.timeSinceUpdateSeconds / 60)} minutes`);
    console.warn(`   Flights displayed: ${flightStatus.flightsDisplayed}`);
    // Use safe reload with backoff
    safeReload('stale_flight_data');
  }
}

/**
 * Self-healing mechanism - attempts recovery without reload first
 */
async function attemptSelfHealing() {
  console.log('🔧 Attempting self-healing...');

  try {
    // Check network connectivity
    const network = await Network.getStatus();
    if (!network.connected) {
      console.error('❌ Network disconnected, waiting for reconnection...');
      setNetworkAvailable(false);
      return;
    }

    // Try sending heartbeat again
    const result = await sendHeartbeat();
    if (result.success) {
      console.log('✅ Self-healing successful');
      setNetworkAvailable(true);
      // Reset backoff after successful self-healing
      if (getTimeSinceLastReload() > 5 * 60 * 1000) {
        resetReloadBackoff();
      }
    } else {
      console.error('❌ Self-healing failed, may need manual intervention');
      setNetworkAvailable(false);

      // If still failing after 10 minutes, try reload as last resort
      const timeSinceLastSuccess = Date.now() - lastHeartbeatSuccess;
      if (timeSinceLastSuccess > 10 * 60 * 1000) {
        console.log('🔄 Attempting reload as last resort...');
        // Use safe reload to prevent storms
        safeReload('self_healing_last_resort');
      }
    }
  } catch (error) {
    console.error('❌ Self-healing error:', error);
  }
}

/**
 * Download and install APK update
 *
 * This function triggers automatic APK download and installation.
 * For fully automated silent updates on remote kiosk devices, the app must be configured as Device Owner:
 *   adb shell dpm set-device-owner com.eximenon.myfids/.MyDeviceAdminReceiver
 *
 * Fallback: If not Device Owner, downloads APK to device and prompts user to install.
 */
async function downloadAndInstallAPK(updateInfo) {
  // Use apkUrl if available, fallback to debUrl (same file for Android)
  const downloadUrl = updateInfo.apkUrl || updateInfo.debUrl;
  const { version, rolloutId } = updateInfo;

  if (!downloadUrl) {
    console.error('❌ No download URL provided!');
    console.error('Update Info:', JSON.stringify(updateInfo, null, 2));
    await reportUpdateStatus(rolloutId, 'failed', 'No download URL provided');
    return;
  }

  console.log(`📥 Downloading APK: ${version}`);
  console.log(`📍 URL: ${downloadUrl}`);
  console.log(`🔐 Rollout ID: ${rolloutId}`);

  try {
    // Check if app is Device Owner for silent installation
    let isDeviceOwner = false;
    try {
      const result = await ApkInstaller.isDeviceOwner();
      isDeviceOwner = result.isDeviceOwner;
      console.log(`🔐 Device Owner status: ${isDeviceOwner}`);
    } catch (error) {
      console.log('⚠️ Could not check Device Owner status (plugin may not be available)');
    }

    // Report download status
    await reportUpdateStatus(rolloutId, 'downloading');

    // Report installing status
    await reportUpdateStatus(rolloutId, 'installing');

    // Store update info for tracking and recovery
    const updateAttempt = {
      version,
      downloadUrl,
      rolloutId,
      timestamp: new Date().toISOString(),
      method: isDeviceOwner ? 'device-owner-silent' : 'manual-prompt',
      deviceId: deviceInfo?.deviceId
    };

    localStorage.setItem('fids_last_update_attempt', JSON.stringify(updateAttempt));
    localStorage.setItem('fids_update_in_progress', 'true');

    if (isDeviceOwner) {
      // Device Owner mode - download and install silently via native plugin
      console.log('🔐 Device Owner mode enabled - downloading and installing silently...');
      console.log(`📥 Download URL: ${downloadUrl}`);

      try {
        const installResult = await ApkInstaller.installApk({ apkUrl: downloadUrl });

        if (installResult.success) {
          console.log('✅ Silent installation initiated successfully');
          console.log('🔄 App will restart automatically with new version');
        } else {
          throw new Error(installResult.message || 'Installation failed');
        }
      } catch (installError) {
        console.error('❌ Silent installation failed:', installError);
        await reportUpdateStatus(rolloutId, 'failed', `Silent install error: ${installError.message}`);
        localStorage.removeItem('fids_update_in_progress');
        return;
      }
    } else {
      // Standard mode - use iframe download (requires user interaction)
      console.log('⚠️ Not Device Owner - using standard download (may require user interaction)');
      console.log('📱 Triggering APK download...');

      // Use hidden iframe to trigger download
      const downloadFrame = document.createElement('iframe');
      downloadFrame.style.display = 'none';
      downloadFrame.src = downloadUrl;
      document.body.appendChild(downloadFrame);

      console.log('📋 APK download initiated - user may need to tap notification to install');
      console.log('👆 User will need to tap "Install" when prompted');
    }

    console.log('');
    console.log('ℹ️  Installation mode:');
    if (isDeviceOwner) {
      console.log('   ✓ Device Owner: Silent auto-install (no prompts)');
    } else {
      console.log('   ⚠️ Standard mode: System installer (user must tap "Install")');
      console.log('   💡 To enable silent updates, configure as Device Owner:');
      console.log('      adb shell dpm set-device-owner com.eximenon.myfids/.MyDeviceAdminReceiver');
    }
    console.log('');
    console.log('🔄 After installation completes, app will restart with new version');

    // Set a recovery timeout - if we're still here after 5 minutes, something went wrong
    setTimeout(async () => {
      const stillInProgress = localStorage.getItem('fids_update_in_progress');
      if (stillInProgress === 'true') {
        console.warn('⚠️  Update did not complete within expected time');
        console.warn('   This might mean:');
        console.warn('   1. Installation is in progress');
        console.warn('   2. Installation is waiting for user interaction');
        console.warn('   3. Update failed silently');

        await reportUpdateStatus(rolloutId, 'failed', 'Update timeout - no confirmation of installation after 5 minutes');
        localStorage.removeItem('fids_update_in_progress');
      }
    }, 5 * 60 * 1000); // 5 minutes

  } catch (error) {
    console.error('❌ APK download/install failed:', error);
    console.error('   Error details:', error.message);
    console.error('   Stack:', error.stack);

    localStorage.removeItem('fids_update_in_progress');
    await reportUpdateStatus(rolloutId, 'failed', `Download error: ${error.message}`);
  }
}

/**
 * Report update status to backend
 */
async function reportUpdateStatus(rolloutId, status, error = null) {
  try {
    const deviceToken = localStorage.getItem('fids_device_token');
    if (!deviceToken || !deviceInfo) {
      return;
    }

    await CapacitorHttp.post({
      url: `${getApiBaseUrl_dynamic()}/api/rollout/device-status`,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${deviceToken}`
      },
      data: {
        deviceId: deviceInfo.deviceId,
        rolloutId,
        status,
        error
      }
    });

    console.log(`📊 Update status reported: ${status}`);
  } catch (err) {
    console.error('Failed to report update status:', err);
  }
}

/**
 * Start health monitoring
 */
export function startHealthMonitoring() {
  if (!getIsAndroid()) {
    console.log('⚠️ Not on Android, health monitoring disabled');
    return;
  }

  if (healthCheckTimer) {
    clearInterval(healthCheckTimer);
  }

  healthCheckTimer = setInterval(healthCheck, HEALTH_CHECK_INTERVAL);
  console.log('🏥 Health monitoring started (every 5s)');
}

/**
 * Stop health monitoring
 */
export function stopHealthMonitoring() {
  if (healthCheckTimer) {
    clearInterval(healthCheckTimer);
    healthCheckTimer = null;
    console.log('🏥 Health monitoring stopped');
  }
}

/**
 * Start auto-reload timer (1 hour)
 * Reloads the app every hour to ensure fresh content and prevent memory leaks
 * Uses safeReload to prevent reload storms when network is unstable
 */
export function startAutoReload() {
  if (!getIsAndroid()) {
    console.log('⚠️ Not on Android, auto-reload disabled');
    return;
  }

  // Stop existing timer if any
  if (autoReloadTimer) {
    clearInterval(autoReloadTimer);
  }

  // Set up hourly reload with safety checks
  autoReloadTimer = setInterval(() => {
    const uptimeHours = Math.floor((Date.now() - appStartTime) / (1000 * 60 * 60));
    console.log(`🔄 Auto-reload check after ${uptimeHours} hours...`);

    // Check if network is available before reloading
    if (!_isNetworkAvailable) {
      console.log('⏳ Auto-reload deferred - network unavailable');
      console.log('   Will retry on next interval');
      return;
    }

    // Use safe reload with backoff protection
    const reloaded = safeReload('auto_hourly');
    if (!reloaded) {
      console.log('⏳ Auto-reload deferred by backoff - will retry on next interval');
    }
  }, AUTO_RELOAD_INTERVAL);

  console.log('🔄 Auto-reload service started (every 1 hour, with backoff protection)');
}

/**
 * Stop auto-reload timer
 */
export function stopAutoReload() {
  if (autoReloadTimer) {
    clearInterval(autoReloadTimer);
    autoReloadTimer = null;
    console.log('🔄 Auto-reload service stopped');
  }
}

/**
 * Calculate milliseconds until next quiet time (4 AM local)
 */
function getMillisUntilQuietTime() {
  const now = new Date();
  const quietTime = new Date(now);
  quietTime.setHours(QUIET_TIME_RESTART_HOUR, 0, 0, 0);

  // If we've passed today's quiet time, schedule for tomorrow
  if (now >= quietTime) {
    quietTime.setDate(quietTime.getDate() + 1);
  }

  return quietTime.getTime() - now.getTime();
}

/**
 * Start daily quiet-time restart scheduler
 * Restarts the app at 4 AM local time for cache clearing and memory management
 */
export function startQuietTimeRestart() {
  if (!getIsAndroid()) {
    console.log('⚠️ Not on Android, quiet-time restart disabled');
    return;
  }

  // Stop existing timer if any
  if (quietTimeRestartTimer) {
    clearTimeout(quietTimeRestartTimer);
  }

  const msUntilRestart = getMillisUntilQuietTime();
  const hoursUntilRestart = Math.round(msUntilRestart / (1000 * 60 * 60) * 10) / 10;

  console.log(`🌙 Quiet-time restart scheduled in ${hoursUntilRestart} hours (${QUIET_TIME_RESTART_HOUR}:00 local)`);

  quietTimeRestartTimer = setTimeout(() => {
    console.log(`🌙 Quiet-time restart triggered (${QUIET_TIME_RESTART_HOUR}:00 AM)`);

    // Clear WebView cache if possible
    try {
      localStorage.setItem('fids_last_quiet_restart', new Date().toISOString());
    } catch (e) {
      // localStorage not available
    }

    // Check if network is available before reloading
    if (!_isNetworkAvailable) {
      console.log('⏳ Quiet-time restart deferred - network unavailable');
      // Reschedule for 30 minutes later
      quietTimeRestartTimer = setTimeout(() => {
        safeReload('quiet_time_deferred');
      }, 30 * 60 * 1000);
      return;
    }

    // Use safe reload with backoff protection
    // For quiet time, we can be more aggressive since it's a maintenance window
    resetReloadBackoff();
    safeReload('quiet_time_maintenance');

    // If reload was blocked, reschedule
    // The safeReload will trigger the reload, so this code path typically won't be reached
  }, msUntilRestart);
}

/**
 * Stop quiet-time restart scheduler
 */
export function stopQuietTimeRestart() {
  if (quietTimeRestartTimer) {
    clearTimeout(quietTimeRestartTimer);
    quietTimeRestartTimer = null;
    console.log('🌙 Quiet-time restart scheduler stopped');
  }
}

/**
 * Initialize all kiosk services
 */
export async function initializeKioskServices() {
  if (!getIsAndroid()) {
    console.log('⚠️ Not on Android, kiosk services disabled');
    return false;
  }

  console.log('🚀 Initializing Android kiosk services...');

  try {
    // Wait for MainActivity to set window variables (give it time)
    await new Promise(resolve => setTimeout(resolve, 150));

    // Store display mode in localStorage for persistence across reloads
    if (window.ANDROID_DISPLAY_MODE && window.ANDROID_START_ROUTE) {
      localStorage.setItem('fids_display_mode', window.ANDROID_DISPLAY_MODE);
      localStorage.setItem('fids_start_route', window.ANDROID_START_ROUTE);
      console.log(`📱 Stored display mode: ${window.ANDROID_DISPLAY_MODE}, route: ${window.ANDROID_START_ROUTE}`);
    }

    // Initialize device info FIRST (required for auto-registration)
    await initializeDeviceInfo();

    // Check if an update was in progress and verify if it completed
    const updateInProgress = localStorage.getItem('fids_update_in_progress');
    const lastUpdateAttempt = localStorage.getItem('fids_last_update_attempt');

    if (updateInProgress === 'true' && lastUpdateAttempt) {
      try {
        const updateInfo = JSON.parse(lastUpdateAttempt);
        const currentVersion = await getAppVersion();

        console.log('🔍 Checking update status...');
        console.log(`   Last update attempt: ${updateInfo.version}`);
        console.log(`   Current version: ${currentVersion}`);

        if (currentVersion === updateInfo.version) {
          // Update succeeded!
          console.log('🎉 UPDATE SUCCESSFUL!');
          console.log(`   ✅ App updated from previous version to ${currentVersion}`);

          // Clear the in-progress flag
          localStorage.removeItem('fids_update_in_progress');

          // Report success to backend
          if (updateInfo.rolloutId) {
            await reportUpdateStatus(updateInfo.rolloutId, 'completed', null);
            console.log('📊 Update success reported to backend');
          }
        } else {
          // Update didn't complete or failed
          console.warn('⚠️  Update did not complete');
          console.warn(`   Expected version: ${updateInfo.version}`);
          console.warn(`   Current version: ${currentVersion}`);
          localStorage.removeItem('fids_update_in_progress');
        }
      } catch (err) {
        console.error('Error checking update status:', err);
        localStorage.removeItem('fids_update_in_progress');
      }
    }

    // Initialize device token (may trigger auto-registration if no token exists)
    await initializeDeviceToken();

    // Start heartbeat
    startHeartbeat();

    // Start health monitoring
    startHealthMonitoring();

    // Initialize UI health monitor (global error handlers + periodic checks)
    initializeErrorHandlers();
    uiHealthCheckCleanup = startPeriodicHealthChecks(30000); // Check every 30 seconds
    console.log('🏥 UI Health Monitor initialized');

    // Start auto-reload timer (1 hour)
    startAutoReload();

    // Start daily quiet-time restart (4 AM)
    startQuietTimeRestart();

    // Listen for app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        console.log('📱 App became active');
        // Send heartbeat when app comes to foreground
        sendHeartbeat();
      } else {
        console.log('📱 App moved to background');
      }
    });

    // Listen for network changes
    Network.addListener('networkStatusChange', (status) => {
      console.log('🌐 Network status changed:', status);
      setNetworkAvailable(status.connected);

      if (status.connected) {
        // Reconnected - send heartbeat
        sendHeartbeat();
      }
    });

    console.log('✅ Kiosk services initialized successfully');
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize kiosk services:', error);
    return false;
  }
}

/**
 * Shutdown kiosk services
 */
export function shutdownKioskServices() {
  stopHeartbeat();
  stopHealthMonitoring();
  stopAutoReload();
  stopQuietTimeRestart();

  // Stop UI health monitor
  if (uiHealthCheckCleanup) {
    uiHealthCheckCleanup();
    uiHealthCheckCleanup = null;
  }

  // Stop registration polling if active
  if (window._registrationPollInterval) {
    clearInterval(window._registrationPollInterval);
    window._registrationPollInterval = null;
  }

  console.log('🛑 Kiosk services shutdown');
}
